import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import {
  useColorModeValue } from "./ui/color-mode";
import {
  Box,
  Flex,
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  Card,
  Input,
  NativeSelect,
  Alert,
  SimpleGrid,
  Popover,
  IconButton,
  Portal,
  Field,
} from '@chakra-ui/react';
import { toaster } from '@/components/ui/toaster';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FaChartLine,
  FaCalendarAlt,
  FaCalendar,
  FaArrowsAltH,
  FaBalanceScale,
  FaRegCommentDots,
  FaThList,
  FaChartPie,
  FaFilePdf,
  FaMagic,
} from 'react-icons/fa';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  Legend,
  BarChart,
  Bar,
  XAxis,
  YAxis,
} from 'recharts';
import AppLayout from './AppLayout.jsx';
import { useProductTour } from '../hooks/useProductTour';
import { bandaMetricasSteps } from '../tours/bandaMetricasTour';
import { bandasAPI } from '../services/api.js';
import { metricasEs } from '../locales/metricas.es.js';
import { openMetricasPdfWindow } from '../utils/metricasPdfExport.js';
import { MetricasInformeHero, MetricasInformeContenido, MOTIVO_TEXTO, ymdLocal } from './MetricasInformeContenido.jsx';
import { LuInfo } from 'react-icons/lu';

const tMetricas = metricasEs;

/** Comparación lexicográfica para YYYY-MM-DD (zona del servidor / API). */
function compareYmdStr(a, b) {
  if (!a || !b) return 0;
  if (a === b) return 0;
  return a < b ? -1 : 1;
}

function clampYmdToSpan(v, low, high) {
  if (!v) return low;
  if (compareYmdStr(v, low) < 0) return low;
  if (compareYmdStr(v, high) > 0) return high;
  return v;
}

const MotionBox = motion.create(Box);
const MotionButton = motion.create(Button);

function WizardProgressBar({ visualStep, totalSteps, sub, tMetricas }) {
  const labels =
    totalSteps === 2
      ? [tMetricas.wizard.stepperL1, tMetricas.wizard.stepperL3]
      : [tMetricas.wizard.stepperL1, tMetricas.wizard.stepperL2, tMetricas.wizard.stepperL3];
  const pct = (visualStep / totalSteps) * 100;
  return (
    <Box mb={1}>
      <Flex justify="space-between" align="center" mb={2.5} gap={2} px={0.5}>
        {labels.map((label, i) => {
          const n = i + 1;
          return (
            <Text
              key={label}
              flex="1 1 0"
              textAlign="center"
              fontSize="xs"
              fontWeight="700"
              letterSpacing="0.05em"
              textTransform="uppercase"
              color={visualStep === n ? 'purple.500' : visualStep > n ? 'purple.400' : sub}
              transition="color 0.3s ease"
            >
              {label}
            </Text>
          );
        })}
      </Flex>
      <Box
        h="8px"
        borderRadius="full"
        bg="blackAlpha.100"
        _dark={{ bg: 'whiteAlpha.200' }}
        overflow="hidden"
        position="relative"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(pct)}
        aria-label={tMetricas.wizard.step(visualStep, totalSteps)}
      >
        <Box
          position="absolute"
          left={0}
          top={0}
          h="100%"
          w={`${pct}%`}
          borderRadius="full"
          bgGradient="linear(to-r, purple.400, purple.500, cyan.400)"
          transition="width 0.45s cubic-bezier(0.4, 0, 0.2, 1)"
          boxShadow="0 0 18px rgba(128, 90, 213, 0.35)"
        />
      </Box>
    </Box>
  );
}

function SelectablePillButton({ selected, onClick, icon, children }) {
  const selBg = useColorModeValue('purple.50', 'rgba(90, 55, 150, 0.25)');
  const idleBg = useColorModeValue('white', 'whiteAlpha.50');
  const hoverBg = useColorModeValue('gray.50', 'whiteAlpha.100');
  return (
    <MotionButton
      whileHover={{ y: -2 }}
      whileTap={{ scale: 0.98 }}
      h="auto"
      py={5}
      px={4}
      w="100%"
      justifyContent="flex-start"
      borderRadius="xl"
      variant="outline"
      colorPalette="purple"
      borderWidth="2px"
      borderColor={selected ? 'purple.500' : 'gray.200'}
      bg={selected ? selBg : idleBg}
      color="inherit"
      onClick={onClick}
      boxShadow={selected ? 'md' : 'sm'}
      _hover={{
        borderColor: 'purple.400',
        boxShadow: 'md',
        bg: selected ? selBg : hoverBg,
      }}
      _dark={{ borderColor: selected ? 'purple.300' : 'whiteAlpha.300' }}
    >
      <HStack align="start" gap={3} w="100%">
        <Box color="purple.500" flexShrink={0} mt={0.5}>
          {icon}
        </Box>
        <Text
          fontSize="sm"
          fontWeight="700"
          lineHeight="short"
          textAlign="left"
          color="gray.800"
          _dark={{ color: 'gray.100' }}
        >
          {children}
        </Text>
      </HStack>
    </MotionButton>
  );
}

function SiNoPills({ value, onChange, onValueChange, yesText, noText }) {
  const setValue = onChange || onValueChange;
  const selBg = useColorModeValue('purple.50', 'rgba(90, 55, 150, 0.25)');
  const MotionR = motion.create(Box);
  return (
    <SimpleGrid columns={{ base: 1, sm: 2 }} gap={3} w="100%">
      {[true, false].map((v) => {
        const selected = value === v;
        return (
          <MotionR
            key={String(v)}
            whileHover={{ y: -2 }}
            whileTap={{ scale: 0.98 }}
            p={5}
            borderRadius="xl"
            borderWidth="2px"
            borderColor={selected ? 'purple.500' : 'gray.200'}
            bg={selected ? selBg : 'transparent'}
            cursor="pointer"
            textAlign="left"
            w="100%"
            boxShadow={selected ? 'md' : 'base'}
            transition="border-color 0.2s, background 0.2s, box-shadow 0.2s"
            _hover={{ borderColor: 'purple.300' }}
            _dark={{ borderColor: selected ? 'purple.300' : 'whiteAlpha.300' }}
            asChild
          >
            <button type="button" onClick={() => setValue?.(v)}>
              <HStack>
                <Box
                  w="22px"
                  h="22px"
                  borderRadius="full"
                  borderWidth="2px"
                  borderColor={selected ? 'purple.500' : 'gray.300'}
                  bg={selected ? 'purple.500' : 'transparent'}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  flexShrink={0}
                  transition="all 0.2s"
                >
                  {selected && <Box w="8px" h="8px" borderRadius="full" bg="white" />}
                </Box>
                <Text fontWeight="700" fontSize="md" lineHeight="short" textAlign="left">
                  {v ? yesText : noText}
                </Text>
              </HStack>
            </button>
          </MotionR>
        );
      })}
    </SimpleGrid>
  );
}

function LegendPopoverButton({ tMetricas, mutedColor }) {
  return (
    <Popover.Root positioning={{
      placement: 'bottom-end'
    }}>
      <Popover.Trigger asChild>
        <IconButton
          aria-label={tMetricas.app.infoButtonAria}
          size="md"
          variant="ghost"
          colorPalette="purple"
          borderRadius="full"><LuInfo /></IconButton>
      </Popover.Trigger>
      <Portal>
        <Popover.Positioner>
          <Popover.Content maxW="md" boxShadow="lg">
            <Popover.Title fontWeight="700" border="0" pb={0}>
              {tMetricas.app.legendTitle}
            </Popover.Title>
            <Popover.Arrow />
            <Popover.CloseTrigger />
            <Popover.Body>
              <VStack align="stretch" gap={3} fontSize="sm" color={mutedColor}>
                <Text fontWeight="600" color="gray.800" _dark={{ color: 'gray.100' }}>
                  {tMetricas.help.intro}
                </Text>
                <Text>{tMetricas.help.modoAnio}</Text>
                <Text>{tMetricas.help.modoMes}</Text>
                <Text>{tMetricas.help.modoRango}</Text>
                <Text>{tMetricas.help.comparativa}</Text>
                <Text>{tMetricas.help.narrativa}</Text>
                <Text>{tMetricas.help.fase7}</Text>
              </VStack>
            </Popover.Body>
          </Popover.Content>
        </Popover.Positioner>
      </Portal>
    </Popover.Root>
  );
}

export default function Metricas() {
  useProductTour('banda-metricas', bandaMetricasSteps, { autoStart: false });
  const now = new Date();
  const cy = now.getFullYear();
  const prevMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const [modo, setModo] = useState('anio');
  const [anio, setAnio] = useState(String(cy - 1));
  const [mes, setMes] = useState(String(prevMonth.getMonth() + 1));
  const [anioMes, setAnioMes] = useState(String(prevMonth.getFullYear()));
  const [desde, setDesde] = useState(`${cy - 1}-01-01`);
  const [hasta, setHasta] = useState(ymdLocal(now));

  const [loading, setLoading] = useState(false);
  const [errRed, setErrRed] = useState(null);
  const [data, setData] = useState(null);
  const [compararAnterior, setCompararAnterior] = useState(true);
  const [incluirNarrativa, setIncluirNarrativa] = useState(false);
  const [opciones, setOpciones] = useState(null);
  const [rangoListo, setRangoListo] = useState(false);
  const [vista, setVista] = useState('asistente');
  const [pasoWizard, setPasoWizard] = useState(1);
  /** `null` = aún desconocido; si `false`, el paso de comparar con el periodo anterior se omite. */
  const [comparativaPosible, setComparativaPosible] = useState(null);
  const [avancePaso1Loading, setAvancePaso1Loading] = useState(false);
  const comparativaPrefetchId = useRef(0);
  const [modoInforme, setModoInforme] = useState(null);
  const [vistaGraficos, setVistaGraficos] = useState(false);
  const [pdfGenerando, setPdfGenerando] = useState(false);
  const [resumenAnimadoLoading, setResumenAnimadoLoading] = useState(false);

  
  const cardBg = useColorModeValue('white', 'gray.700');
  const sub = useColorModeValue('gray.600', 'gray.400');
  const wizardSurface = useColorModeValue(
    'linear-gradient(165deg, #faf5ff 0%, #ffffff 50%, #f0fdfa 100%)',
    'linear-gradient(165deg, rgba(88, 28, 135, 0.2) 0%, rgba(23, 25, 35, 0.95) 55%, rgba(6, 78, 78, 0.12) 100%)',
  );
  const wizardBorder = useColorModeValue('purple.100', 'whiteAlpha.200');
  const wizardInnerBoxBg = useColorModeValue('whiteAlpha.900', 'blackAlpha.400');
  const wizardFieldBorder = useColorModeValue('purple.200', 'whiteAlpha.300');
  const informeHeroBg = useColorModeValue(
    'linear-gradient(135deg, #faf5ff 0%, #ffffff 45%, #ecfeff 100%)',
    'linear-gradient(135deg, rgba(88, 28, 135, 0.22) 0%, rgba(23, 25, 35, 0.96) 55%, rgba(6, 78, 78, 0.12) 100%)',
  );
  const informeHeroBorder = useColorModeValue('purple.100', 'whiteAlpha.200');

  /** Meses de calendario permitidos para el año `anioMes` (entre alta de banda y hoy, en la TZ de la API). */
  const mesesElegibles = useMemo(() => {
    if (!opciones?.bandaAltaYmd || !opciones?.hoyYmd) {
      return Array.from({ length: 12 }, (_, i) => i + 1);
    }
    const ySel = parseInt(anioMes, 10);
    if (Number.isNaN(ySel)) return Array.from({ length: 12 }, (_, i) => i + 1);
    const alta = opciones.bandaAltaYmd;
    const hoy = opciones.hoyYmd;
    const yAlta = parseInt(alta.slice(0, 4), 10);
    const mAlta = parseInt(alta.slice(5, 7), 10);
    const yHoy = parseInt(hoy.slice(0, 4), 10);
    const mHoy = parseInt(hoy.slice(5, 7), 10);
    let minM = 1;
    let maxM = 12;
    if (ySel === yAlta) minM = mAlta;
    if (ySel === yHoy) maxM = mHoy;
    if (minM > maxM) return [minM];
    return Array.from({ length: maxM - minM + 1 }, (_, i) => minM + i);
  }, [opciones, anioMes]);

  useEffect(() => {
    if (!opciones?.bandaAltaYmd || !opciones?.hoyYmd) return;
    const alta = opciones.bandaAltaYmd;
    const hoy = opciones.hoyYmd;
    setDesde((d) => clampYmdToSpan(d, alta, hoy));
    setHasta((h) => clampYmdToSpan(h, alta, hoy));
  }, [opciones]);

  useEffect(() => {
    if (!desde || !hasta) return;
    if (compareYmdStr(desde, hasta) > 0) setHasta(desde);
  }, [desde, hasta]);

  useEffect(() => {
    if (modo !== 'mes' || mesesElegibles.length === 0) return;
    const m = parseInt(mes, 10);
    if (!Number.isNaN(m) && mesesElegibles.includes(m)) return;
    setMes(String(mesesElegibles[mesesElegibles.length - 1]));
  }, [modo, mes, mesesElegibles]);

  const aplicar = useCallback(async () => {
    if (modo === 'anio' && (!anio || Number.isNaN(parseInt(anio, 10)))) {
      setData(null);
      return;
    }
    setLoading(true);
    setErrRed(null);
    try {
      const baseParams =
        modo === 'anio'
          ? { modo, anio: parseInt(anio, 10) }
          : modo === 'mes'
            ? { modo, mes: parseInt(mes, 10), anioMes: parseInt(anioMes, 10) }
            : { modo, desde, hasta };
      const params = {
        ...baseParams,
        ...(compararAnterior ? { compararAnterior: true } : {}),
        ...(incluirNarrativa ? { narrativa: true } : {}),
      };
      const { data: body } = await bandasAPI.getMetricasResumen(params);
      setData(body);
      setModoInforme(
        modo === 'anio'
          ? { tipo: 'anio', anio: parseInt(anio, 10) }
          : modo === 'mes'
            ? { tipo: 'mes', mes: parseInt(mes, 10), anioMes: parseInt(anioMes, 10) }
            : { tipo: 'rango' },
      );
      setVistaGraficos(false);
      setVista('resultados');
    } catch (e) {
      setErrRed(e?.response?.data?.message || e?.message || 'Error al calcular el rango');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [modo, anio, mes, anioMes, desde, hasta, compararAnterior, incluirNarrativa]);

  const abrirResumenAnimado = useCallback(async () => {
    if (!data?.ok || !data.resumen) {
      toaster.create({
        title: 'Sin datos',
        description: 'Genera un informe con datos antes de abrir el resumen animado.',
        type: 'warning',
      });
      return;
    }
    setResumenAnimadoLoading(true);
    try {
      const body =
        modo === 'anio'
          ? { modo, anio: parseInt(anio, 10), compararAnterior, incluirNarrativa }
          : modo === 'mes'
            ? {
                modo,
                mes: parseInt(mes, 10),
                anioMes: parseInt(anioMes, 10),
                compararAnterior,
                incluirNarrativa,
              }
            : { modo, desde, hasta, compararAnterior, incluirNarrativa };
      const { data: r } = await bandasAPI.postMetricasShareLink(body);
      if (!r?.token) {
        throw new Error('Respuesta sin token');
      }
      const url = `${window.location.origin}/metricas/resumen?t=${encodeURIComponent(r.token)}`;
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      toaster.create({
        title: 'No se pudo generar el enlace',
        description: e?.response?.data?.message || e?.message || 'Inténtalo de nuevo.',
        type: 'error',
      });
    } finally {
      setResumenAnimadoLoading(false);
    }
  }, [
    data,
    modo,
    anio,
    mes,
    anioMes,
    desde,
    hasta,
    compararAnterior,
    incluirNarrativa,
  ]);

  const exportarMetricasPdf = useCallback(
    async (conGraficos) => {
      if (!data) return;
      setPdfGenerando(true);
      try {
        await openMetricasPdfWindow({
          data,
          modoInforme,
          withCharts: conGraficos,
          t: tMetricas,
        });
      } catch (e) {
        if (e?.code === 'NO_POPUP') {
          toaster.create({ title: tMetricas.informe.pdfPopup, type: 'warning' });
        } else {
          toaster.create({ title: tMetricas.informe.pdfError, type: 'error' });
        }
      } finally {
        setPdfGenerando(false);
      }
    },
    [data, modoInforme],
  );

  const reiniciarAsistente = useCallback(() => {
    setVista('asistente');
    setPasoWizard(1);
    setComparativaPosible(null);
    setCompararAnterior(true);
    setData(null);
    setModoInforme(null);
    setVistaGraficos(false);
    setPdfGenerando(false);
    setErrRed(null);
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      const d = new Date();
      d.setMonth(d.getMonth() - 1);
      const anioDelMesElegible = d.getFullYear();
      try {
        setErrRed(null);
        const { data: op } = await bandasAPI.getMetricasOpciones();
        if (!alive) return;
        setOpciones(op);
        if (op.aniosCerrados?.length) {
          setAnio(String(op.aniosCerrados[0]));
        } else {
          setAnio('');
        }
        if (op.aniosHastaHoy?.length) {
          setAnioMes(
            String(
              op.aniosHastaHoy.includes(anioDelMesElegible)
                ? anioDelMesElegible
                : op.aniosHastaHoy[0],
            ),
          );
        }
      } catch (e) {
        if (alive) {
          setErrRed(
            e?.response?.data?.message || e?.message || 'Error al cargar años disponibles',
          );
        }
      } finally {
        if (alive) setRangoListo(true);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const siguienteDeshabilitadoPaso1 =
    !rangoListo ||
    (modo === 'anio' && (!opciones || !anio || opciones.aniosCerrados.length === 0));

  const metricasQueryBase = useMemo(() => {
    if (modo === 'anio') {
      const y = parseInt(anio, 10);
      if (!anio || Number.isNaN(y)) return null;
      return { modo, anio: y };
    }
    if (modo === 'mes') {
      const m = parseInt(mes, 10);
      const y = parseInt(anioMes, 10);
      if (Number.isNaN(m) || Number.isNaN(y)) return null;
      return { modo, mes: m, anioMes: y };
    }
    if (modo === 'rango' && desde && hasta) {
      return { modo, desde, hasta };
    }
    return null;
  }, [modo, anio, mes, anioMes, desde, hasta]);

  useEffect(() => {
    if (vista !== 'asistente' || pasoWizard !== 1) {
      return;
    }
    if (!rangoListo || siguienteDeshabilitadoPaso1 || !metricasQueryBase) {
      setComparativaPosible(null);
      return;
    }
    const id = ++comparativaPrefetchId.current;
    const t = setTimeout(() => {
      (async () => {
        try {
          const { data: rango } = await bandasAPI.getMetricasRango(metricasQueryBase);
          if (id !== comparativaPrefetchId.current) return;
          if (rango?.ok) {
            setComparativaPosible(Boolean(rango.comparativaPosible));
          } else {
            setComparativaPosible(null);
          }
        } catch {
          if (id === comparativaPrefetchId.current) {
            setComparativaPosible(null);
          }
        }
      })();
    }, 400);
    return () => {
      clearTimeout(t);
      comparativaPrefetchId.current += 1;
    };
  }, [vista, pasoWizard, rangoListo, siguienteDeshabilitadoPaso1, metricasQueryBase]);

  const avanzarDesdePaso1 = useCallback(async () => {
    if (siguienteDeshabilitadoPaso1 || !metricasQueryBase) {
      if (!metricasQueryBase) {
        toaster.create({
          title: 'Revisa el periodo',
          description: 'Indica un periodo completo (año, mes o fechas) antes de continuar.',
          type: 'warning',
        });
      }
      return;
    }
    setErrRed(null);
    setAvancePaso1Loading(true);
    try {
      const { data: rango } = await bandasAPI.getMetricasRango(metricasQueryBase);
      if (!rango?.ok) {
        const m = rango?.motivoRechazo;
        toaster.create({
          title: 'Rango no válido',
          description: (m && MOTIVO_TEXTO[m]) || rango?.motivoRechazo || 'No se puede usar este periodo.',
          type: 'warning',
        });
        return;
      }
      const okComparar = Boolean(rango.comparativaPosible);
      setComparativaPosible(okComparar);
      if (okComparar) {
        setPasoWizard(2);
      } else {
        setCompararAnterior(false);
        setPasoWizard(3);
      }
    } catch (e) {
      toaster.create({
        title: 'Error al validar el periodo',
        description: e?.response?.data?.message || e?.message || 'Error de red',
        type: 'error',
      });
    } finally {
      setAvancePaso1Loading(false);
    }
  }, [metricasQueryBase, siguienteDeshabilitadoPaso1]);

  const totalWizardSteps = comparativaPosible === false ? 2 : 3;
  const wizardVisualStep =
    comparativaPosible === false
      ? pasoWizard === 1
        ? 1
        : 2
      : pasoWizard;

  return (
    <AppLayout userType="banda">
      <Box maxW="5xl" mx="auto" py={{ base: 4, md: 8 }} px={4}>
        <VStack align="stretch" gap={6}>
          <Flex justify="space-between" align="flex-start" flexWrap="wrap" gap={3} data-tour="metricas-header">
            <HStack gap={3} align="flex-start">
              <Box color="purple.500" pt={1}>
                <FaChartLine size={28} />
              </Box>
              <Box>
                <HStack gap={1} align="center">
                  <Heading size="lg">{tMetricas.app.title}</Heading>
                  <LegendPopoverButton tMetricas={tMetricas} mutedColor={sub} />
                </HStack>
                <Text color={sub} fontSize="sm" mt={1} maxW="3xl">
                  {tMetricas.app.subtitle}
                </Text>
              </Box>
            </HStack>
            {vista === 'resultados' && (
              <Button variant="outline" colorPalette="purple" onClick={reiniciarAsistente}>
                {tMetricas.wizard.regen}
              </Button>
            )}
          </Flex>

          {vista === 'asistente' && (
            <Box
              position="relative"
              borderRadius="2xl"
              overflow="hidden"
              boxShadow="0 10px 45px -12px rgba(103, 65, 188, 0.38)"
              borderWidth="1px"
              borderColor={wizardBorder}
              bg={wizardSurface}
              data-tour="metricas-wizard"
            >
              <Box
                h="5px"
                bgGradient="linear(to-r, purple.500, purple.400, cyan.400)"
                aria-hidden
              />
              <Card.Root bg="transparent" shadow="none" border="none" borderRadius="none">
                <Card.Header
                  pb={2}
                  pt={{ base: 5, md: 7 }}
                  px={{ base: 4, md: 8 }}
                >
                  <WizardProgressBar
                    visualStep={wizardVisualStep}
                    totalSteps={totalWizardSteps}
                    sub={sub}
                    tMetricas={tMetricas}
                  />
                  <Text
                    fontSize="xs"
                    fontWeight="800"
                    color="purple.500"
                    textTransform="uppercase"
                    letterSpacing="0.2em"
                    mt={4}
                  >
                    {tMetricas.wizard.step(wizardVisualStep, totalWizardSteps)}
                  </Text>
                </Card.Header>
                <Card.Body pt={0} px={{ base: 4, md: 8 }} pb={{ base: 7, md: 9 }}>
                  <AnimatePresence mode="wait">
                    <MotionBox
                      key={`${pasoWizard}-${totalWizardSteps}`}
                      initial={{ opacity: 0, x: 22 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -16 }}
                      transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
                    >
                      <VStack align="stretch" gap={5}>
                        {pasoWizard === 1 && (
                          <>
                            <Box
                              p={{ base: 4, md: 5 }}
                              borderRadius="2xl"
                              borderWidth="1px"
                              borderColor="purple.200"
                              bgGradient="linear(to-br, purple.50, white, cyan.50)"
                              _dark={{
                                borderColor: 'whiteAlpha.200',
                                bgGradient: 'linear(to-br, rgba(88,28,135,0.28), rgba(0,0,0,0.32), rgba(6,78,78,0.18))',
                              }}
                              boxShadow="0 14px 50px -28px rgba(103, 65, 188, 0.55)"
                            >
                              <HStack gap={3} align="flex-start">
                                <Box color="purple.500" pt={0.5}>
                                  <FaMagic size={22} />
                                </Box>
                                <Box flex="1" minW={0}>
                                  <Text fontWeight="900" fontSize={{ base: 'md', md: 'lg' }} lineHeight="short">
                                    Qué es “Estadísticas” y qué vas a sacar de aquí
                                  </Text>
                                  <Text color={sub} fontSize="sm" mt={1}>
                                    Genera informes claros para entender la asistencia, la actividad y la evolución de tu banda
                                    en un periodo concreto. Puedes exportarlo a PDF o compartir un resumen.
                                  </Text>
                                </Box>
                              </HStack>

                              <SimpleGrid columns={{ base: 1, md: 3 }} gap={3} mt={4}>
                                <Box
                                  p={3}
                                  borderRadius="xl"
                                  bg="whiteAlpha.800"
                                  _dark={{ bg: 'whiteAlpha.100' }}
                                  borderWidth="1px"
                                  borderColor="whiteAlpha.400"
                                >
                                  <HStack gap={2} mb={1}>
                                    <Box color="purple.500">
                                      <FaChartPie size={16} />
                                    </Box>
                                    <Text fontWeight="800" fontSize="sm">
                                      Resúmenes rápidos
                                    </Text>
                                  </HStack>
                                  <Text fontSize="sm" color={sub}>
                                    Tasas, totales y comparativas por periodo para ver tendencias de un vistazo.
                                  </Text>
                                </Box>
                                <Box
                                  p={3}
                                  borderRadius="xl"
                                  bg="whiteAlpha.800"
                                  _dark={{ bg: 'whiteAlpha.100' }}
                                  borderWidth="1px"
                                  borderColor="whiteAlpha.400"
                                >
                                  <HStack gap={2} mb={1}>
                                    <Box color="purple.500">
                                      <FaRegCommentDots size={16} />
                                    </Box>
                                    <Text fontWeight="800" fontSize="sm">
                                      Insights accionables
                                    </Text>
                                  </HStack>
                                  <Text fontSize="sm" color={sub}>
                                    Detecta puntos de mejora: asistencia, constancia y patrones habituales.
                                  </Text>
                                </Box>
                                <Box
                                  p={3}
                                  borderRadius="xl"
                                  bg="whiteAlpha.800"
                                  _dark={{ bg: 'whiteAlpha.100' }}
                                  borderWidth="1px"
                                  borderColor="whiteAlpha.400"
                                >
                                  <HStack gap={2} mb={1}>
                                    <Box color="purple.500">
                                      <FaFilePdf size={16} />
                                    </Box>
                                    <Text fontWeight="800" fontSize="sm">
                                      Exportar / compartir
                                    </Text>
                                  </HStack>
                                  <Text fontSize="sm" color={sub}>
                                    Descarga un PDF listo para enviar o comparte un resumen tipo “wrapped”.
                                  </Text>
                                </Box>
                              </SimpleGrid>
                            </Box>

                            <HStack align="start" gap={2}>
                              <Box color="purple.500" display={{ base: 'none', sm: 'block' }} mt={0.5}>
                                <FaCalendarAlt size={20} />
                              </Box>
                              <Text
                                as="h2"
                                fontWeight="800"
                                fontSize={{ base: 'lg', md: 'xl' }}
                                lineHeight="short"
                                letterSpacing="-0.01em"
                              >
                                {tMetricas.wizard.step1Title}
                              </Text>
                            </HStack>
                            <SimpleGrid columns={{ base: 1, md: 3 }} gap={3} w="100%">
                              <SelectablePillButton
                                selected={modo === 'anio'}
                                onClick={() => setModo('anio')}
                                icon={<FaCalendarAlt size={22} />}
                              >
                                {tMetricas.wizard.modoAnio}
                              </SelectablePillButton>
                              <SelectablePillButton
                                selected={modo === 'mes'}
                                onClick={() => setModo('mes')}
                                icon={<FaCalendar size={22} />}
                              >
                                {tMetricas.wizard.modoMes}
                              </SelectablePillButton>
                              <SelectablePillButton
                                selected={modo === 'rango'}
                                onClick={() => setModo('rango')}
                                icon={<FaArrowsAltH size={22} />}
                              >
                                {tMetricas.wizard.modoRango}
                              </SelectablePillButton>
                            </SimpleGrid>

                            <Box
                              p={{ base: 3, md: 4 }}
                              borderRadius="xl"
                              borderWidth="1px"
                              borderColor={wizardBorder}
                              bg={wizardInnerBoxBg}
                            >
                              <AnimatePresence mode="wait" initial={false}>
                                {modo === 'anio' && (
                                  <MotionBox
                                    key="anio"
                                    initial={{ opacity: 0, y: 8 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -6 }}
                                    transition={{ duration: 0.2 }}
                                  >
                                    <Field.Root maxW="xs">
                                      <Field.Label fontWeight="600">Año</Field.Label>
                                      <NativeSelect.Root>
                                        <NativeSelect.Field
                                          value={anio}
                                          onChange={(e) => setAnio(e.target.value)}
                                          disabled={!opciones || (opciones.aniosCerrados || []).length === 0}
                                          placeholder={!opciones ? 'Cargando…' : 'Elige un año'}
                                          borderColor={wizardFieldBorder}
                                          _hover={{ borderColor: 'purple.300' }}>
                                          {(opciones?.aniosCerrados ?? []).map((y) => (
                                            <option key={y} value={String(y)}>
                                              {y}
                                            </option>
                                          ))}
                                        </NativeSelect.Field>
                                        <NativeSelect.Indicator />
                                      </NativeSelect.Root>
                                      {opciones && opciones.aniosCerrados.length === 0 && (
                                        <Text fontSize="xs" color={sub} mt={1}>
                                          No hay ningún año civil completo aún: solo años ya cerrados
                                          (anterior al año en curso) y con solape con el alta de la banda.
                                        </Text>
                                      )}
                                    </Field.Root>
                                  </MotionBox>
                                )}
                                {modo === 'mes' && (
                                  <MotionBox
                                    key="mes"
                                    initial={{ opacity: 0, y: 8 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -6 }}
                                    transition={{ duration: 0.2 }}
                                  >
                                    <HStack align="flex-end" flexWrap="wrap" gap={4}>
                                      <Field.Root maxW="xs">
                                        <Field.Label fontWeight="600">Mes</Field.Label>
                                        <NativeSelect.Root>
                                          <NativeSelect.Field
                                            value={mes}
                                            onChange={(e) => setMes(e.target.value)}
                                            borderColor={wizardFieldBorder}>
                                            {mesesElegibles.map((mNum) => (
                                              <option key={mNum} value={String(mNum)}>
                                                {mNum}
                                              </option>
                                            ))}
                                          </NativeSelect.Field>
                                          <NativeSelect.Indicator />
                                        </NativeSelect.Root>
                                      </Field.Root>
                                      <Field.Root maxW="xs">
                                        <Field.Label fontWeight="600">Año</Field.Label>
                                        <NativeSelect.Root>
                                          <NativeSelect.Field
                                            value={anioMes}
                                            onChange={(e) => setAnioMes(e.target.value)}
                                            disabled={!opciones || (opciones.aniosHastaHoy || []).length === 0}
                                            placeholder={!opciones ? 'Cargando…' : 'Elige un año'}
                                            borderColor={wizardFieldBorder}>
                                            {(opciones?.aniosHastaHoy ?? []).map((y) => (
                                              <option key={y} value={String(y)}>
                                                {y}
                                              </option>
                                            ))}
                                          </NativeSelect.Field>
                                          <NativeSelect.Indicator />
                                        </NativeSelect.Root>
                                      </Field.Root>
                                    </HStack>
                                  </MotionBox>
                                )}
                                {modo === 'rango' && (
                                  <MotionBox
                                    key="rango"
                                    initial={{ opacity: 0, y: 8 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -6 }}
                                    transition={{ duration: 0.2 }}
                                  >
                                    <HStack align="flex-end" flexWrap="wrap" gap={4}>
                                      <Field.Root maxW="xs">
                                        <Field.Label fontWeight="600">Desde</Field.Label>
                                        <Input
                                          type="date"
                                          value={desde}
                                          min={opciones?.bandaAltaYmd}
                                          max={opciones?.hoyYmd}
                                          onChange={(e) => {
                                            const v = e.target.value;
                                            setDesde(v);
                                            setHasta((h) => (compareYmdStr(v, h) > 0 ? v : h));
                                          }}
                                          borderColor={wizardFieldBorder}
                                        />
                                      </Field.Root>
                                      <Field.Root maxW="xs">
                                        <Field.Label fontWeight="600">Hasta</Field.Label>
                                        <Input
                                          type="date"
                                          value={hasta}
                                          min={desde || opciones?.bandaAltaYmd}
                                          max={opciones?.hoyYmd}
                                          onChange={(e) => {
                                            let v = e.target.value;
                                            if (opciones?.bandaAltaYmd && opciones?.hoyYmd) {
                                              v = clampYmdToSpan(v, opciones.bandaAltaYmd, opciones.hoyYmd);
                                            }
                                            if (desde && compareYmdStr(v, desde) < 0) v = desde;
                                            setHasta(v);
                                          }}
                                          borderColor={wizardFieldBorder}
                                        />
                                      </Field.Root>
                                    </HStack>
                                  </MotionBox>
                                )}
                              </AnimatePresence>
                            </Box>

                            <HStack justify="flex-end" pt={1}>
                              <Button
                                colorPalette="purple"
                                size="lg"
                                onClick={avanzarDesdePaso1}
                                loading={avancePaso1Loading}
                                disabled={siguienteDeshabilitadoPaso1}
                                px={8}
                                boxShadow="md"
                                _hover={{ boxShadow: 'lg', transform: 'translateY(-1px)' }}
                                transition="all 0.2s"
                              >
                                {tMetricas.wizard.next}
                              </Button>
                            </HStack>
                          </>
                        )}

                        {pasoWizard === 2 && (
                          <>
                            <HStack align="start" gap={2}>
                              <Box color="cyan.500" display={{ base: 'none', sm: 'block' }} mt={0.5}>
                                <FaBalanceScale size={20} />
                              </Box>
                              <VStack align="start" gap={0}>
                                <Text
                                  as="h2"
                                  fontWeight="800"
                                  fontSize={{ base: 'lg', md: 'xl' }}
                                  lineHeight="short"
                                >
                                  {tMetricas.wizard.step2Title}
                                </Text>
                                <Text fontSize="sm" color={sub} mt={1.5} lineHeight="tall">
                                  {tMetricas.wizard.step2Hint}
                                </Text>
                              </VStack>
                            </HStack>
                            <SiNoPills
                              value={compararAnterior}
                              onValueChange={setCompararAnterior}
                              yesText={tMetricas.wizard.step2Si}
                              noText={tMetricas.wizard.step2No}
                            />
                            <HStack justify="space-between" pt={2} flexWrap="wrap" gap={2}>
                              <Button variant="ghost" size="md" onClick={() => setPasoWizard(1)}>
                                {tMetricas.wizard.back}
                              </Button>
                              <Button
                                colorPalette="purple"
                                size="lg"
                                onClick={() => setPasoWizard(3)}
                                boxShadow="md"
                                px={8}
                                _hover={{ boxShadow: 'lg' }}
                              >
                                {tMetricas.wizard.next}
                              </Button>
                            </HStack>
                          </>
                        )}

                        {pasoWizard === 3 && (
                          <>
                            <HStack align="start" gap={2}>
                              <Box color="purple.500" display={{ base: 'none', sm: 'block' }} mt={0.5}>
                                <FaRegCommentDots size={20} />
                              </Box>
                              <VStack align="start" gap={0}>
                                <Text
                                  as="h2"
                                  fontWeight="800"
                                  fontSize={{ base: 'lg', md: 'xl' }}
                                  lineHeight="short"
                                >
                                  {tMetricas.wizard.step3Title}
                                </Text>
                                <Text fontSize="sm" color={sub} mt={1.5} lineHeight="tall">
                                  {tMetricas.wizard.step3Hint}
                                </Text>
                              </VStack>
                            </HStack>
                            <SiNoPills
                              value={incluirNarrativa}
                              onValueChange={setIncluirNarrativa}
                              yesText={tMetricas.wizard.step3Si}
                              noText={tMetricas.wizard.step3No}
                            />
                            <HStack justify="space-between" pt={2} flexWrap="wrap" gap={2}>
                              <Button
                                variant="ghost"
                                size="md"
                                onClick={() => setPasoWizard(comparativaPosible === true ? 2 : 1)}
                              >
                                {tMetricas.wizard.back}
                              </Button>
                              <Button
                                colorPalette="purple"
                                size="lg"
                                onClick={aplicar}
                                loading={loading}
                                loadingText="Consultando…"
                                px={8}
                                boxShadow="lg"
                                _hover={{ boxShadow: 'xl' }}
                                bgGradient="linear(to-r, purple.500, purple.600)"
                                _active={{ transform: 'scale(0.98)' }}
                              >
                                {tMetricas.wizard.consult}
                              </Button>
                            </HStack>
                          </>
                        )}
                      </VStack>
                    </MotionBox>
                  </AnimatePresence>
                </Card.Body>
              </Card.Root>
            </Box>
          )}

          {errRed && (
            <Alert.Root status="error" borderRadius="md">
              <Alert.Indicator />
              {errRed}
            </Alert.Root>
          )}

          {vista === 'resultados' && data && !errRed && (
            <>
              <MetricasInformeHero
                data={data}
                modoInforme={modoInforme}
                tMetricas={tMetricas}
                sub={sub}
                informeHeroBorder={informeHeroBorder}
                informeHeroBg={informeHeroBg}
              />
              {data.resumen && data.ok && !data.vacio && (
                <MetricasInformeContenido
                  data={data}
                  tMetricas={tMetricas}
                  sub={sub}
                  cardBg={cardBg}
                  vistaGraficos={vistaGraficos}
                  setVistaGraficos={setVistaGraficos}
                  showToolbar
                  pdfGenerando={pdfGenerando}
                  exportarMetricasPdf={exportarMetricasPdf}
                  abrirResumenAnimado={abrirResumenAnimado}
                  resumenAnimadoLoading={resumenAnimadoLoading}
                  incluirNarrativa={incluirNarrativa}
                />
              )}
            </>
          )}

        </VStack>
      </Box>
    </AppLayout>
  );
}
